import { useState } from 'react';

import { SimpleModal } from '../../../shared/SimpleModal';
import { DANGER_RED } from '../../primitives';

import { CancelReasonField } from './CancelReasonField';

export interface CancelConfirmDialogProps {
  /** Confirmed bookings moving, and their total attendees. */
  bookings: number;
  attendees: number;
  /** "Type · Venue · Date" of the chosen target, or null when nothing moves. */
  targetLabel: string | null;
  errorMessage: string | null;
  processing: boolean;
  onBack: () => void;
  onConfirm: (reason: string) => void;
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** The last step: what will happen, a required reason, and the commit button. */
export function CancelConfirmDialog({
  bookings,
  attendees,
  targetLabel,
  errorMessage,
  processing,
  onBack,
  onConfirm,
}: CancelConfirmDialogProps) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const missingReason = reason.trim().length === 0;

  const submit = () => {
    setTouched(true);
    if (missingReason || processing) return;
    onConfirm(reason.trim());
  };

  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
      <button type="button" className="nb-btn nb-btn-ghost" onClick={onBack} disabled={processing}>
        Back
      </button>
      <button
        type="button"
        className="nb-btn"
        onClick={submit}
        disabled={processing}
        style={{ color: '#fff', background: DANGER_RED, borderColor: DANGER_RED }}
      >
        {targetLabel ? `Cancel workshop & notify ${bookings}` : 'Cancel workshop'}
      </button>
    </div>
  );

  return (
    <SimpleModal
      open
      onClose={processing ? () => undefined : onBack}
      title="Cancel this workshop?"
      footer={footer}
    >
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
        {targetLabel ? (
          <>
            {plural(bookings, 'booking')} ({plural(attendees, 'attendee')}) will move to{' '}
            <strong>{targetLabel}</strong>, and each customer will be emailed the new details. This
            can’t be undone.
          </>
        ) : (
          <>
            This workshop has no confirmed bookings, so nobody will be moved. This can’t be undone.
          </>
        )}
      </p>
      <CancelReasonField
        value={reason}
        onChange={setReason}
        onBlur={() => setTouched(true)}
        showMissing={touched && missingReason}
      />
      {errorMessage && (
        <div role="alert" style={{ color: DANGER_RED, fontSize: 13, marginTop: 10 }}>
          {errorMessage}
        </div>
      )}
    </SimpleModal>
  );
}
